import { __awaiter } from "tslib";
import { createFile, ItemsEntity, UserRole } from '@citrite/sf-api';
import { RID } from '@sharefiledev/sfplugin-resource-id';
import { shouldDisableLegacyRoles } from '../constants';
import { DefaultProjectDataProvider } from '../PluginDataProvider/DefaultProjectDataProvider';
import { ProjectUserRole, ResourceEntityType } from '../types';
import { projectsExtensionSlots } from './extensionTypes';
import { uploadFiles } from './uploadFiles';
const projectsCapabilities = {
    provideResourceId(requestedByResourceId) {
        return __awaiter(this, void 0, void 0, function* () {
            const projectRid = getProjectRid(requestedByResourceId);
            return projectRid.toString();
        });
    },
    getFileActions(requestedByResourceId) {
        return {
            uploadFiles(files, options) {
                return __awaiter(this, void 0, void 0, function* () {
                    return uploadFiles(DefaultProjectDataProvider, requestedByResourceId, files, options);
                });
            },
            deleteFiles(...resourceIds) {
                return __awaiter(this, void 0, void 0, function* () {
                    const projectId = getProjectIdFromRid(requestedByResourceId);
                    const fileIds = getFileIdsFromRids(resourceIds);
                    yield DefaultProjectDataProvider.deleteProjectFiles(projectId, fileIds);
                });
            },
            getDownloadUrl(...resourceIds) {
                return __awaiter(this, void 0, void 0, function* () {
                    const projectId = getProjectIdFromRid(requestedByResourceId);
                    const fileIds = getFileIdsFromRids(resourceIds);
                    const downloadSpec = yield DefaultProjectDataProvider.getProjectFilesBulkDownloadUrl(projectId, fileIds);
                    if (!downloadSpec.DownloadUrl) {
                        throw new Error('No download url returned');
                    }
                    return downloadSpec.DownloadUrl;
                });
            },
            getContentViewerProps(...resourceIds) {
                return __awaiter(this, void 0, void 0, function* () {
                    const resourceId = resourceIds[0];
                    const fileRid = RID.parse(resourceId).findEntityType(ResourceEntityType.fileStream);
                    if (!(fileRid === null || fileRid === void 0 ? void 0 : fileRid.entityId)) {
                        throw new Error(`No stream id found for: ${resourceId}`);
                    }
                    const itemUrl = ItemsEntity.get(fileRid.entityId).getUrl();
                    const contentViewerProps = {
                        item: createFile({
                            url: itemUrl,
                            FileName: '',
                            Name: '',
                        }),
                        isNativeWorkspace: false,
                        isWorkspace: false,
                        hideNote: true,
                        disableEditFileName: true,
                    };
                    return contentViewerProps;
                });
            },
        };
    },
    getAuthorizedUserIds(requestedByResourceId) {
        return __awaiter(this, void 0, void 0, function* () {
            const projectId = getProjectIdFromRid(requestedByResourceId);
            const projectUsers = yield DefaultProjectDataProvider.getProjectUsers(projectId);
            return projectUsers.map(({ user }) => user.id);
        });
    },
    getNavigationDetails(requestedByResourceId_1) {
        return __awaiter(this, arguments, void 0, function* (requestedByResourceId, options = {}) {
            var _a;
            const projectId = getProjectIdFromRid(requestedByResourceId);
            const projectEntity = yield DefaultProjectDataProvider.getProjectById(projectId);
            let webAppPath = `/projects/${projectEntity.id}`;
            const extensionSlot = (_a = options.targetExtensionSlot) !== null && _a !== void 0 ? _a : projectsExtensionSlots.tabs;
            if (extensionSlot === projectsExtensionSlots.tabs && options.targetExtensionSlotId) {
                webAppPath = `${webAppPath}/${options.targetExtensionSlotId}`;
            }
            return {
                name: projectEntity.name,
                webAppPath,
            };
        });
    },
};
function getProjectRid(resourceId) {
    const rid = RID.parse(resourceId);
    const projectRid = rid.findEntityType(ResourceEntityType.project);
    if (!projectRid) {
        throw new Error(`No project resource found for: ${resourceId}`);
    }
    return projectRid;
}
const getProjectIdFromRid = (resourceId) => getProjectRid(resourceId).entityId;
const getFileIdsFromRids = (resourceIds) => resourceIds
    .map(rid => {
    const file = RID.parse(rid).findEntityType(ResourceEntityType.file);
    return file === null || file === void 0 ? void 0 : file.entityId;
})
    .filter(Boolean);
export const capabilities = {
    [ResourceEntityType.project]: projectsCapabilities,
};
/**
 * @param users Only the Id field is required. Pass full objects only if you already have them, don't fetch
 */
export function addUsersToProject(requestedByResourceId, users, projectUserRoleToAdd) {
    return __awaiter(this, void 0, void 0, function* () {
        const projectId = getProjectIdFromRid(requestedByResourceId);
        const projectUsers = yield DefaultProjectDataProvider.getProjectUsers(projectId);
        const usersToAdd = [];
        for (const user of users) {
            const userId = user.Id;
            if (!userId) {
                // Invalid user id
                continue;
            }
            if (projectUsers.some(existingUser => existingUser.user.id === userId)) {
                // Already on the project
                continue;
            }
            let projectUserRole = ProjectUserRole.Client;
            if (projectUserRoleToAdd) {
                // If project user role is provided in params then use that
                projectUserRole = projectUserRoleToAdd;
            }
            else if (user.Roles &&
                user.Roles.includes(UserRole.Employee) &&
                !shouldDisableLegacyRoles()) {
                // Counterpart is referred as Team Member in the UI
                projectUserRole = ProjectUserRole.Counterpart;
            }
            else if (!user.Roles) {
                try {
                    const role = yield DefaultProjectDataProvider.getRoleByUser(userId);
                    if (role !== UserRole.Client) {
                        projectUserRole = ProjectUserRole.Counterpart;
                    }
                }
                catch (_error) {
                    /** @todo log this */
                }
            }
            usersToAdd.push({ user: { id: userId }, role: projectUserRole });
        }
        if (!usersToAdd.length) {
            return [];
        }
        try {
            yield DefaultProjectDataProvider.addUsersToProject(projectId, {
                projectUsers: usersToAdd,
            });
        }
        catch (_error) {
            /** @todo log this */
        }
        return usersToAdd.map(u => u.user.id);
    });
}
export function getProjectContacts(rid) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const contacts = yield DefaultProjectDataProvider.getProjectContacts(rid);
            return contacts;
        }
        catch (_a) {
            throw new Error('Could not get contacts');
        }
    });
}
//# sourceMappingURL=capabilities.js.map